import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FacebookTemplateVariableMappingEditor } from '@/components/settings/FacebookTemplateVariableMappingEditor';
import {
  buildFacebookFormSourceOptions,
  buildFacebookVariableMapping,
  countMetaTemplateBodyVariables,
  normalizeFacebookVariableMapping,
  type FacebookLeadgenQuestion,
  type FacebookVariableMappingEntry,
  type MetaTemplateLike,
} from '@/components/settings/facebookTemplateMapping';

interface LeadgenForm {
    id: string;
    name: string;
    status: string;
    locale: string;
    questions?: FacebookLeadgenQuestion[];
}

interface WABA {
    id: string;
    name: string;
}

interface Template extends MetaTemplateLike {
    status: string;
}

interface FormLinkDialogProps {
    form: LeadgenForm | null;
    pageId: string;
    isOpen: boolean;
    onClose: () => void;
    onLinkSuccess: () => void;
}

export function FormLinkDialog({ form, pageId, isOpen, onClose, onLinkSuccess }: FormLinkDialogProps) {
    const { token } = useAuth();
    const [wabas, setWabas] = useState<WABA[]>([]);
    const [selectedWaba, setSelectedWaba] = useState<string>('');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [variableMapping, setVariableMapping] = useState<FacebookVariableMappingEntry[]>([]);
    const [loadingWabas, setLoadingWabas] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sourceOptions = useMemo(
        () => buildFacebookFormSourceOptions(form?.questions),
        [form?.questions],
    );

    const selectedTemplateObj = templates.find((template) => template.name === selectedTemplate) ?? null;
    const selectedTemplateBody = selectedTemplateObj?.components?.find(
        (component) => component.type?.toUpperCase() === 'BODY',
    )?.text;

    useEffect(() => {
        if (isOpen) {
            setError(null);
            setSelectedWaba('');
            setSelectedTemplate('');
            setTemplates([]);
            setVariableMapping([]);
            fetchWabas();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (!selectedTemplateObj) {
            setVariableMapping([]);
            return;
        }
        const count = countMetaTemplateBodyVariables(selectedTemplateObj);
        setVariableMapping((previous) => buildFacebookVariableMapping(count, previous));
    }, [selectedTemplateObj]);

    const fetchWabas = async () => {
        setLoadingWabas(true);
        try {
            const response = await fetch('/api/v1/leads/meta/waba/', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch WABAs');

            const data = await response.json();
            const businesses = data.data || [];
            const allWabas: WABA[] = [];

            interface Business {
                owned_whatsapp_business_accounts?: {
                    data: WABA[];
                };
            }
            businesses.forEach((biz: Business) => {
                if (biz.owned_whatsapp_business_accounts && biz.owned_whatsapp_business_accounts.data) {
                    allWabas.push(...biz.owned_whatsapp_business_accounts.data);
                }
            });

            setWabas(allWabas);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch WABAs');
        } finally {
            setLoadingWabas(false);
        }
    };

    const fetchTemplates = async (wabaId: string) => {
        setLoadingTemplates(true);
        try {
            const response = await fetch(`/api/v1/leads/meta/templates/?waba_id=${wabaId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch templates');

            const data = await response.json();
            setTemplates(data.data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch templates');
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleWabaChange = (value: string) => {
        setSelectedWaba(value);
        setSelectedTemplate('');
        setVariableMapping([]);
        fetchTemplates(value);
    };

    const handleTemplateChange = (value: string) => {
        setSelectedTemplate(value);
        setVariableMapping([]);
    };

    const handleLink = async () => {
        if (!form || !selectedWaba || !selectedTemplate) return;

        const normalizedMapping = normalizeFacebookVariableMapping(variableMapping);
        if (variableMapping.length > 0 && normalizedMapping.length !== variableMapping.length) {
            setError('Completa el mapeo de todas las variables de la plantilla.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const template = templates.find(t => t.name === selectedTemplate);
            if (!template) throw new Error("Selected template not found");

            const payload: Record<string, unknown> = {
                form_id: form.id,
                form_name: form.name,
                page_id: pageId,
                waba_id: selectedWaba,
                template_name: template.name,
                template_language: template.language,
            };

            if (normalizedMapping.length > 0) {
                payload.variable_mapping = normalizedMapping;
            }
            if (form.questions?.length) {
                payload.form_questions_snapshot = form.questions;
            }

            const response = await fetch('/api/v1/leads/meta/form-links/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                const mappingError = Array.isArray(errorData.variable_mapping)
                    ? errorData.variable_mapping[0]
                    : null;
                throw new Error(mappingError || errorData.error || 'Failed to link form');
            }

            onLinkSuccess();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to link form');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Link Form to Template</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="grid gap-2">
                        <Label>Form</Label>
                        <div className="p-2 border rounded-md bg-muted text-sm">
                            {form?.name} ({form?.id})
                        </div>
                    </div>

                    <div className="grid gap-2">
                        <Label>WhatsApp Business Account</Label>
                        <Select value={selectedWaba} onValueChange={handleWabaChange} disabled={loadingWabas}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select WABA" />
                            </SelectTrigger>
                            <SelectContent>
                                {wabas.map((waba) => (
                                    <SelectItem key={waba.id} value={waba.id}>
                                        {waba.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-2">
                        <Label>Message Template</Label>
                        <Select
                            value={selectedTemplate}
                            onValueChange={handleTemplateChange}
                            disabled={!selectedWaba || loadingTemplates}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select Template" />
                            </SelectTrigger>
                            <SelectContent>
                                {templates.map((template) => (
                                    <SelectItem key={template.name} value={template.name}>
                                        {template.name} ({template.language}) — {countMetaTemplateBodyVariables(template)} vars
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedTemplateBody ? (
                        <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground whitespace-pre-wrap">
                            {selectedTemplateBody}
                        </p>
                    ) : null}

                    <FacebookTemplateVariableMappingEditor
                        mapping={variableMapping}
                        sourceOptions={sourceOptions}
                        onChange={setVariableMapping}
                        showHelp
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleLink} disabled={submitting || !selectedTemplate}>
                        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Link Template
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
